import { Component, DestroyRef, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CostModelDialogBodyComponent } from '@components/dialogs/cost-model-dialog-body/cost-model-dialog-body.component';
import { CostingGridResponse, GridOverrides, RowSelection } from '@mds/common';
import {
  ButtonComponent,
  ConfirmationDialogComponent,
  DialogModule,
  GridCellCostingComponent,
  GridModule,
  GridNoRowsComponent,
  PotentialSavingsCellRendererComponent
} from '@mds/ui';
import { LayersService } from '@pages/dashboards/scope-3/services/layers.service';
import { FormSchema } from '@pages/mattermaps/components/layers/components/costing-grid-wrapper/costing-grid-wrapper.model';
import { AnalyzeFiltersService } from '@services/analyze.filters.service';
import { AnalyzeService } from '@services/analyze.service';
import { EventDelegatorService } from '@services/event-delegator.service';
import { GlobalDataService } from '@services/global-data.service';
import { ComposerApiService } from '@services/composer-api.service';
import { MattermapApiService } from '@services/mattermap-api.service';
import { ReusableService } from '@services/reusable.service';
import { ToastrService } from '@services/toaster.service';
import { GridApi, RowDoubleClickedEvent, RowHeightParams } from 'ag-grid-community';
import { ColDef } from 'ag-grid-enterprise';
import _ from 'lodash';
import { concat } from 'rxjs';
import { getRowStyle } from '../../../../utils/get-row-style';

@Component({
  selector: 'app-costing-grid-wrapper',
  standalone: true,
  imports: [ButtonComponent, CostModelDialogBodyComponent, DialogModule, GridModule],
  templateUrl: './costing-grid-wrapper.component.html',
  styleUrls: ['./costing-grid-wrapper.component.scss']
})
export class CostingGridWrapperComponent implements OnInit {
  destroyRef = inject(DestroyRef);

  @ViewChild('costModelBody') costModelBody!: TemplateRef<any>;
  @ViewChild('costModelActions') costModelActions!: TemplateRef<any>;

  private readonly gridSettingsLabel = 'costing';

  gridApi: GridApi;
  overrides: GridOverrides;
  rowData: any;
  header: any;
  activeLayer: any;
  showAllAssociatedData: any;

  public data: any;
  public isDrillingMode = false;
  public costModelFormSchema: any = [];
  public formSchema: any[] = [];
  public costModelDialogData: any;
  public costModelFormValues = null;
  private costModelDialogRef: MatDialogRef<ConfirmationDialogComponent>;
  private path: any[] = [];
  private pins: any[] = [];
  private analyzeData: any;
  private ngxEventBus: any[] = [];

  constructor(
    private analyzeService: AnalyzeService,
    private eventDelegatorService: EventDelegatorService,
    private mattermapApiService: MattermapApiService,
    private composerApiService: ComposerApiService,
    private layersService: LayersService,
    private reusableService: ReusableService,
    private dialog: MatDialog,
    private toastrService: ToastrService,
    private globalDataService: GlobalDataService,
    private analyzeFiltersService: AnalyzeFiltersService
  ) {}

  ngOnInit(): void {
    this.showAllAssociatedData = !this.globalDataService.getAnalysisFilter();

    this.analyzeService
      .getAnalyzeData()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        setTimeout(() => {
          if (value) {
            this.analyzeData = value;
            this.init();
          }
        }, 300);
      });

    this.analyzeService
      .getBreadcrumbPath()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        this.path = res;
      });

    this.analyzeService
      .getPinsAsObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        this.pins = res;
      });

    this.globalDataService
      .getActiveApp()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        this.activeLayer = data;
      });

    this.ngxEventBus.push(
      this.eventDelegatorService.on('ngx-event-analyze', (ev) => {
        if (ev.action === 'toggle-all-switch') {
          this.showAllAssociatedData = !this.globalDataService.getAnalysisFilter();
          this.init();
        }
      })
    );
  }

  showAllData(): void {
    this.showAllAssociatedData = !this.globalDataService.getAnalysisFilter();
    this.init();
  }

  applyTariff(): void {
    this.getCostTableWithPins();
  }

  init(isDrill = false): void {
    const allActivePins = this.pins.filter((e) => e.checkbox && !e.hidden);
    const rid = this.analyzeService.getAnalyzeRid();

    this.isDrillingMode = isDrill;
    this.analyzeService.setGridDrillingMode(true);

    if (allActivePins.length > 0 || this.path.length > 1) {
      this.getCostTableWithPins();
    } else {
      this.getCostTable(rid);
    }
  }

  getCostTable(rid): void {
    this.mattermapApiService.getCostTable(rid).subscribe((res: CostingGridResponse) => {
      this.showGrid(res);
    });
  }

  getCostTableWithPins(): void {
    const payload: any = {
      pins: this.pins.filter((e) => e.checkbox && !e.hidden) || [],
      ...(this.globalDataService.getTariffApplied() ? this.globalDataService.getAnalysisTariff() : {})
    };
    const lastNode = _.cloneDeep(this.path[this.path.length - 1]);

    if (this.path.length > 1) {
      delete lastNode.sourceLinks;
      delete lastNode.targetLinks;
      payload.node = lastNode;
    }

    this.mattermapApiService.applyPinsOnCostTable(lastNode.rid, payload).subscribe((res: CostingGridResponse) => {
      this.showGrid(res);
    });
  }

  showGrid(res: CostingGridResponse): void {
    this.analyzeData.isToggleOnCostTable = !!res.toggle_on_field;
    this.analyzeService.setGridDrillingMode(true);

    this.createCostModelForm(res.formSchema);

    if (this.isDrillingMode) {
      this.appendDrillRows(res);
    } else {
      res.layer = 'costing';
      this.data = res;
      this.analyzeService.setLayer(res.layer);
      this.initCosting();
    }
  }

  private appendDrillRows(res: CostingGridResponse): void {
    const newRows = _.cloneDeep(res.rows || []);
    this.removeTotalRow(newRows);

    // The drilled node is the last entry in the breadcrumb path
    const drilledNode = this.path[this.path.length - 1];
    const drilledRid = drilledNode?.rid?.replace('#', '');

    // Get the drilled node's processed parents from the grid (root-to-leaf order)
    const existingRow = this.rowData?.find((r) => r.rid?.replace('#', '') === drilledRid);
    const parentPrefix = existingRow?.parents || [];

    // The first row in the response is the drilled node itself (already in the grid).
    // Remove it so we don't create a duplicate.
    const rootRow = newRows[0];
    const childRows = rootRow.rid?.replace('#', '') === drilledRid ? newRows.slice(1) : newRows;

    // Process new rows through getCostTableRowData to get grid-ready format
    const processedRows = this.reusableService.getCostTableRowData(childRows, this.data.formSchema.cost_model);

    // Fix parents: replace the 'ROOT' reference (= drilled node) with the actual parent path.
    // getCostTableRowData reverses parents when parents[0] !== 'ROOT', so the processed rows
    // now have parents in root-to-leaf order (same as parentPrefix). We need to replace the
    // drilled node reference with the full path.
    processedRows.forEach((row) => {
      if (row.parents) {
        // After getCostTableRowData processing, parents are in root-to-leaf order.
        // The drilled node appears as 'ROOT' (kept at index 0 by getCostTableRowData
        // since parents[0] === 'ROOT') or as the drilled node's id somewhere in the array.
        // Replace the 'ROOT' reference with the full parent path of the drilled node.
        const filteredParents = row.parents.filter((p) => p !== 'ROOT');
        row.parents = [...parentPrefix, ...filteredParents];
      }
    });

    const filteredRows = this.analyzeFiltersService.getFilteredRows(
      processedRows,
      this.activeLayer,
      this.showAllAssociatedData,
      this.data.toggle_on_field
    );

    this.gridApi.applyTransaction({ add: filteredRows });

    this.rowData = [...this.rowData, ...filteredRows];
  }

  initCosting(): void {
    const nodes = _.cloneDeep(this.data.rows);

    this.removeTotalRow(nodes);

    this.layersService.setTariffSchema(this.data?.tariff_schema);

    const rawData = nodes.length > 1 ? nodes[1].raw : nodes[0].raw;
    this.header = nodes.length ? this.getHeader(rawData, this.data.header) : [];
    this.rowData = nodes.length ? this.reusableService.getCostTableRowData(nodes, this.data.formSchema.cost_model) : [];

    if (this.rowData.some((row) => row.bom_line)) {
      this.reusableService.pradicateBy(this.rowData, 'bom_line');
    }

    this.rowData = this.analyzeFiltersService.getFilteredRows(
      this.rowData,
      this.activeLayer,
      this.showAllAssociatedData,
      this.data.toggle_on_field
    );

    const autoGroupColumnDef =
      nodes.length < 1
        ? false
        : {
            headerName: 'Name',
            field: 'name',
            minWidth: 500,
            cellRenderer: 'agGroupCellRenderer',
            cellRendererParams: {
              suppressCount: true,
              innerRenderer: GridCellCostingComponent,
              onClick: (params: any) => this.openCostModelDialog(params)
            }
          };

    this.overrides = {
      columnDefs: this.header,
      rowData: this.rowData,
      defaultColDef: {
        flex: 1,
        minWidth: 100,
        enableValue: true,
        enablePivot: true,
        sortable: true,
        filter: true,
        resizable: true
      },
      sideBar: {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel'
          }
        ]
      },
      autoGroupColumnDef: autoGroupColumnDef as ColDef<any>,
      rowSelection: RowSelection.SINGLE,
      treeGrid: true,
      getDataPath: (data) => data.parents,
      noRowsOverlayComponent: GridNoRowsComponent,
      getRowHeight: (params: RowHeightParams) => 96,
      getRowStyle
    };
    this.setGridState();
  }

  getHeader(node, schema) {
    const headers = [];

    this.reusableService.pradicateBy(schema, 'sortlevel');

    if (!schema) return headers;

    schema.forEach((e) => {
      const colObj: Record<string, any> = {
        field: e.key,
        headerName: e.value || e.label,
        hide: e.hidden || false
      };

      colObj.minWidth = 150;

      if (['in', 'out'].includes(colObj.field)) {
        colObj.minWidth = 350;
      }

      if (colObj.field === 'pot_saving') {
        colObj.cellRenderer = PotentialSavingsCellRendererComponent;
      }

      let fieldValue;
      if (node[colObj.field]) {
        fieldValue = node[colObj.field];
      }

      if (typeof fieldValue === 'number') {
        colObj.filter = 'agNumberColumnFilter';

        if (this.data.layer !== 'costing') {
          colObj.cellRenderer = 'ScientificNotationRendererComponent';
        }

        colObj.comparator = function (num1, num2) {
          if (num1 === null && num2 === null) return 0;
          if (num1 === null || isNaN(num1)) return -1;
          if (num2 === null || isNaN(num2)) return 1;
          return num1 - num2;
        };
      }

      if (typeof fieldValue === 'string') {
        colObj.filter = 'agTextColumnFilter';
      }

      if (!['name', 'original_name'].includes(colObj.field) && colObj?.headerName !== '') {
        headers.push(colObj);
      }
    });

    const eObj = {
      headerName: 'Action',
      field: '',
      cellRenderer: 'ActionCellRendererComponent',
      pinned: 'left',
      width: 75
    };

    if (this.data.layer !== 'costing') {
      headers.push(eObj);
    }

    return headers;
  }

  createCostModelForm(form: FormSchema): void {
    this.formSchema = [{ name: 'Costing Options', formSchema: form.process_type?.concat(form.cost_model) }];
    this.costModelFormSchema = form;
  }

  drillDown(event): void {
    if (!event.event.node.allLeafChildren.length) {
      const node = event.data;
      const rid = this.analyzeService.getAnalyzeRid();
      const lastNode = _.cloneDeep(this.path[this.path.length - 1]);

      if (lastNode?.rid?.replace('#', '') === node.rid?.replace('#', '') || node.rid?.replace('#', '') === rid) {
        return;
      }

      event.event.node.setExpanded(true);

      this.analyzeService.updateBreadcrumbPath(event.data);
      this.init(true);
    }
  }

  openCostModelDialog(event: any): void {
    const rowData = event.data;

    this.costModelDialogData = {
      formSchema: this.formSchema,
      allFormSchema: this.costModelFormSchema,
      rowData,
      filterData: this.pins.filter((e) => e.checkbox && !e.hidden) || []
    };

    this.costModelDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'cost-model',
      data: {
        title: 'Customize cost model',
        subTitle: rowData.name,
        content: '',
        body: this.costModelBody,
        actions: this.costModelActions
      }
    });

    this.costModelDialogRef.afterClosed().subscribe((dialogResponse) => {
      this.handleCostModelResponse(dialogResponse, rowData);
      this.costModelDialogData = null;
      this.costModelFormValues = null;
    });
  }

  onCostModelFormValuesChange(event: any): void {
    this.costModelFormValues = event;
  }

  onUpdateModel(): void {
    if (!this.costModelFormValues.valid) {
      return;
    }

    const responseModel = {
      button: 'saveAndUpdate',
      data: this.costModelFormValues
    };
    this.costModelDialogRef.close(responseModel);
  }

  handleCostModelResponse(dialogResponse, rowData: any): void {
    if (!dialogResponse) return;

    if (dialogResponse?.button === 'saveAndUpdate') {
      this.saveAndUpdateGrid(dialogResponse, rowData);
    } else {
      this.submitCostModel(rowData, dialogResponse);
    }
  }

  saveAndUpdateGrid(dialogResponse, rowData: any): void {
    const rid = rowData?.rid;

    if (rid) {
      const formData = this.flattenFormData(dialogResponse.data.value);
      const submitFormData = this.composerApiService.submitEditedMetadata(rid, formData);
      const mainRid = this.analyzeService.getAnalyzeRid();
      let payload = {};

      if (this.globalDataService.getTariffApplied()) {
        payload = {
          key: mainRid,
          data: {
            pins: this.pins.filter((e) => e.checkbox && !e.hidden),
            ...this.globalDataService.getAnalysisTariff()
          }
        };
      }

      concat(submitFormData, this.mattermapApiService.editCostModel(mainRid, payload)).subscribe((val: any) => {
        if (val?.rows) {
          this.analyzeService.updateBreadcrumbPath([this.path[0]]);

          this.isDrillingMode = false;
          this.showGrid(val);
        }
      });
    }
  }

  flattenFormData(formData: any) {
    if (!formData) {
      return null;
    }

    return Object.keys(formData).reduce((acc, curr) => ({ ...acc, ...formData[curr] }), {});
  }

  submitCostModel(rowData, pins): void {
    const rid = this.analyzeService.getAnalyzeRid();
    const payload: Record<string, any> = {
      data: {
        pins,
        node: rowData
      },
      rid: rowData.rid
    };

    this.mattermapApiService.editCostModel(rid, payload).subscribe((res: any) => {
      const costPins = res.pins;
      const badPinIndex = costPins.findIndex((e) => e.error);

      if (badPinIndex !== -1) {
        const badPin = costPins.splice(badPinIndex, 1);
        this.toastrService.show(`The cost model for the pin on ${badPin.label} does not exist`, 5000);
      }

      res.layer = 'costing';
      this.data = res;
    });
  }

  removeTotalRow(rows: any[]): void {
    if (rows.length && rows[0].row_type === 'total') {
      rows.splice(0, 1);
    }
  }

  onGridReady(gridApi: GridApi) {
    this.gridApi = gridApi;
  }

  onSelectionChange() {
    const obj = {
      rows: this.gridApi.getSelectedRows(),
      nodes: this.gridApi.getSelectedNodes(),
      type: 'selection',
      controller: window.location.pathname
    };
    this.globalDataService.nodeEvent = obj.nodes[0];
  }

  onRowDoubleClicked(event: RowDoubleClickedEvent) {
    const gridEventData = {
      event,
      data: event.data,
      type: 'rowDoubleClick',
      controller: window.location.pathname
    };
    this.drillDown(gridEventData); // old behavior
  }

  private expandNodes(nodeIds: string[]): void {
    this.gridApi.forEachNodeAfterFilterAndSort((node) => {
      node.setExpanded((nodeIds || []).includes(node.id));
    });

    this.gridApi.redrawRows();
  }

  private getExpandedRowIds(): string[] {
    const expandedRowIds: string[] = [];

    this.gridApi?.forEachNode((node) => {
      if (node.expanded) expandedRowIds.push(node.id);
    });

    return expandedRowIds;
  }

  onGridStateChanged(): void {
    if (!this.gridApi) return;

    const gridSetting = {
      columnState: this.gridApi?.getColumnState(),
      filterSettings: this.gridApi?.getFilterModel(),
      pivotMode: this.gridApi?.isPivotMode(),
      expandedNodes: this.getExpandedRowIds()
    };

    this.globalDataService.setGridStatus(this.gridSettingsLabel, gridSetting);
  }

  setGridState(): void {
    const gridSettings = this.globalDataService.getGridStatus(this.gridSettingsLabel);

    if (!gridSettings || !this.gridApi) {
      return;
    }

    setTimeout(() => {
      const { filterSettings, columnState, pivotMode, expandedNodes } = gridSettings;

      if (filterSettings) this.gridApi.setFilterModel(filterSettings);
      if (columnState) this.gridApi.applyColumnState({ state: columnState, applyOrder: true });
      if (pivotMode) this.gridApi.setGridOption('pivotMode', pivotMode);
      if (expandedNodes) {
        this.expandNodes(expandedNodes);
      }
    }, 500);
  }
}
